import {
  useCallback,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import moment from "moment/moment";
import {
  IconBuilding,
  IconChevronDown,
  IconChevronRight,
  IconDownload,
  IconLink,
  IconPlus,
} from "@tabler/icons-react";
import Button from "../UI/Button";
import { Table } from "../UI/Table";
import Text, { type Color } from "../UI/Text";
import FilterBar from "../UI/FilterBar/FilterBar";
import FilterSection from "../UI/FilterBar/FilterSection";
import StatusCell from "../Pilotage/StatusCell";
import StatusFilterLabel from "../Pilotage/StatusFilterLabel";
import PilotageHeader from "../Pilotage/PilotageHeader";
import FilterableColumnHeader from "../Pilotage/FilterableColumnHeader";
import DiagnosticsKpis, { type DiagnosticsCounts } from "./DiagnosticsKpis";
import usePropertyDiagnostics, {
  usePropertyFolderDiagnostics,
} from "../../hooks/use-property-diagnostics";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
import useTableSort from "../../hooks/use-table-sort";
import { naturalCompare } from "../../utils/sort";
import { downloadCsv } from "../../utils/csv";
// @ts-expect-error pure js
import { buildPropertyFoldersTree } from "../../utils/properties";
// @ts-expect-error pure js
import { DIAGNOSTIC_TAGS } from "../../utils/constants/documents";
// @ts-expect-error pure js
import { DOCUMENT_LIST } from "../../models/documents";
import {
  type DiagnosticSource,
  type DiagnosticStatusCode,
  getDiagnosticStatus,
  type PropertyDiagnostic,
  resolvePropertyDiagnostic,
} from "../../utils/documentStatus";
import {
  DIAGNOSTIC_SHORT_LABELS,
  type DiagnosticRequirement,
  getDiagnosticRequirement,
} from "../../utils/diagnosticRules";
import { PropertyStatus, type Property } from "../../models/property";
import type { PropertyFolder } from "../../models/propertyFolder";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import DiagnosticFilesDialog from "./DiagnosticFilesDialog";
import styles from "./DiagnosticsSynthese.module.css";

const DIAGNOSTIC_TAG_LIST = DIAGNOSTIC_TAGS as string[];

const STATUS_COLORS: Record<DiagnosticStatusCode, Color> = {
  manquant: "neutral",
  expire: "danger",
  bientotExpire: "warning",
  valide: "success",
  nonConcerne: "neutral",
};

const STATUS_LABELS: Record<DiagnosticStatusCode, string> = {
  manquant: "À ajouter",
  expire: "Expiré",
  bientotExpire: "Expire bientôt",
  valide: "Valide",
  nonConcerne: "Non concerné",
};

// « Non requis » quand la dispense est déduite du bien (motif), « Non concerné » quand elle est manuelle.
function getNonConcerneLabel({ reason }: PropertyDiagnostic): string {
  return reason ? "Non requis" : STATUS_LABELS.nonConcerne;
}

// « Hérité » n'est pas un état du document mais son origine : on le propose quand même au filtre.
const HERITE = "herite";

const STATUS_FILTER_OPTIONS = [
  {
    value: "manquant",
    label: (
      <StatusFilterLabel text="À ajouter" color="neutral" icon={IconPlus} />
    ),
  },
  {
    value: "expire",
    label: <StatusFilterLabel text="Expiré" color="danger" />,
  },
  {
    value: "bientotExpire",
    label: <StatusFilterLabel text="Expire bientôt" color="warning" />,
  },
  {
    value: "valide",
    label: <StatusFilterLabel text="Valide" color="success" />,
  },
  {
    value: HERITE,
    label: <StatusFilterLabel text="Hérité" color="info" icon={IconLink} />,
  },
  { value: "nonConcerne", label: <StatusFilterLabel text="Non requis" /> },
];

const matchesStatusFilter = (
  diagnostic: PropertyDiagnostic,
  selected: string[],
) =>
  selected.length === 0 ||
  selected.some(
    (value) =>
      value === diagnostic.status ||
      (value === HERITE && diagnostic.inheritedFrom !== undefined),
  );

const formatDate = (date: string) => moment(date).format("DD/MM/YYYY");

function getStatusText(diagnostic: PropertyDiagnostic): string {
  const { status, validUntil } = diagnostic;
  if (status === "bientotExpire" && validUntil) {
    const days = moment(validUntil).diff(moment().startOf("day"), "days");
    return days <= 0 ? "Expire aujourd'hui" : `Expire J-${days}`;
  }
  if (status === "nonConcerne") {
    return getNonConcerneLabel(diagnostic);
  }
  return STATUS_LABELS[status];
}

function getValidityText({
  status,
  validUntil,
  reason,
}: PropertyDiagnostic): string {
  if (status === "manquant") {
    return reason
      ? `${reason} · aucun document : cliquer pour l'ajouter`
      : "Aucun document : cliquer pour l'ajouter";
  }
  if (status === "nonConcerne") {
    return reason ?? "Ce bien est dispensé de ce diagnostic";
  }
  if (!validUntil) {
    return "Sans date de validité";
  }
  if (status === "expire") {
    return `Expiré depuis le ${formatDate(validUntil)}`;
  }
  return `Valide jusqu'au ${formatDate(validUntil)}`;
}

function getTooltip(diagnostic: PropertyDiagnostic): string {
  const validity = getValidityText(diagnostic);
  return diagnostic.inheritedFrom
    ? `Hérité de ${diagnostic.inheritedFrom} · ${validity}`
    : validity;
}

function getExportText(diagnostic: PropertyDiagnostic): string {
  const text = diagnostic.inheritedFrom
    ? `Hérité de ${diagnostic.inheritedFrom}`
    : diagnostic.status === "nonConcerne"
    ? getNonConcerneLabel(diagnostic)
    : STATUS_LABELS[diagnostic.status];
  return diagnostic.validUntil
    ? `${text} (jusqu'au ${formatDate(diagnostic.validUntil)})`
    : text;
}

function getDocumentLabel(tag: string): string {
  return (
    (DOCUMENT_LIST as { tag: string; nom: string }[]).find(
      (document) => document.tag === tag,
    )?.nom ?? tag
  );
}

type Diagnostics = Record<string, PropertyDiagnostic>;

type Row =
  | {
      kind: "folder";
      folder: PropertyFolder;
      depth: number;
      ancestors: PropertyFolder[];
      statuses: Diagnostics;
    }
  | {
      kind: "property";
      property: Property;
      depth: number;
      ancestors: PropertyFolder[];
      statuses: Diagnostics;
      requirements: Record<string, DiagnosticRequirement>;
    };

interface SelectedDiagnostic {
  tag: string;
  property?: Property;
  propertyFolder?: PropertyFolder;
  ancestors: PropertyFolder[];
  requirement?: DiagnosticRequirement;
}

interface DiagnosticsSyntheseProps {
  properties: Property[];
  propertyFolders: PropertyFolder[];
  /** Filtre « Biens et dossiers » de la page, partagé avec les autres onglets. */
  bienFilter?: ReactNode;
  bienFilterCount: number;
  onClearBienFilter: () => void;
}

/**
 * Onglet Diagnostics du pilotage : les dossiers (immeubles) avec leurs biens en
 * dessous, et pour chaque bien l'état de chaque diagnostic technique exigible
 * d'après ses caractéristiques (type, année de construction, zone de bruit…).
 * Un bien sans document propre hérite de celui de son immeuble ; une colonne
 * dont aucun bien affiché n'est concerné est masquée.
 */
export default function DiagnosticsSynthese({
  properties,
  propertyFolders,
  bienFilter,
  bienFilterCount,
  onClearBienFilter,
}: DiagnosticsSyntheseProps) {
  const { diagnosticsByProperty, mutate: mutateProperties } =
    usePropertyDiagnostics();
  const { diagnosticsByFolder, mutate: mutateFolders } =
    usePropertyFolderDiagnostics();
  const { updateProperty } = useProperties() as {
    updateProperty: (id: number, values: Partial<Property>) => Promise<unknown>;
  };
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(
    {},
  );
  const [collapsedFolders, setCollapsedFolders] = useState<Set<number>>(
    () => new Set(),
  );
  const [selected, setSelected] = useState<SelectedDiagnostic | null>(null);
  const { sort, toggleSort, directionFor } =
    useTableSort<"nom">("diagnostics_sort");

  const hasColumnFilter = Object.values(columnFilters).some(
    (values) => values.length > 0,
  );

  // Même règle que la fiche du bien : un bien hérite des documents partagés de
  // son dossier parent direct.
  const toSources = useCallback(
    (ancestors: PropertyFolder[]): DiagnosticSource[] =>
      ancestors.slice(0, 1).map((folder) => ({
        name: folder.nom,
        files: (diagnosticsByFolder[folder.id] ?? []).filter(
          (file) => file.shared,
        ),
      })),
    [diagnosticsByFolder],
  );

  // Toutes les lignes qui répondent à la recherche, avant le filtre de statut :
  // les tuiles de synthèse comptent sur cette base.
  const allRows = useMemo(() => {
    const searchLower = search.toLowerCase();
    const direction = sort?.direction === "desc" ? -1 : 1;
    const byName = (a: { nom: string }, b: { nom: string }) =>
      direction * naturalCompare(a.nom, b.nom);
    const tree = buildPropertyFoldersTree(propertyFolders, properties) as {
      property_folders: PropertyFolder[];
      properties: Property[];
    };

    const result: Row[] = [];
    const walk = (
      folders: PropertyFolder[],
      folderProperties: Property[],
      depth: number,
      ancestors: PropertyFolder[],
    ) => {
      [...folders].sort(byName).forEach((folder) => {
        result.push({
          kind: "folder",
          folder,
          depth,
          ancestors,
          statuses: Object.fromEntries(
            DIAGNOSTIC_TAG_LIST.map((tag) => [
              tag,
              getDiagnosticStatus(diagnosticsByFolder[folder.id], tag),
            ]),
          ),
        });
        walk(folder.property_folders, folder.properties, depth + 1, [
          folder,
          ...ancestors,
        ]);
      });
      [...folderProperties]
        .filter((property) => property.status === PropertyStatus.Actif.status)
        .filter(
          (property) =>
            property.nom.toLowerCase().includes(searchLower) ||
            ancestors.some((folder) =>
              folder.nom.toLowerCase().includes(searchLower),
            ),
        )
        .sort(byName)
        .forEach((property) => {
          const requirements = Object.fromEntries(
            DIAGNOSTIC_TAG_LIST.map((tag) => [
              tag,
              getDiagnosticRequirement(property, tag),
            ]),
          );
          result.push({
            kind: "property",
            property,
            depth,
            ancestors,
            requirements,
            statuses: Object.fromEntries(
              DIAGNOSTIC_TAG_LIST.map((tag) => [
                tag,
                resolvePropertyDiagnostic(tag, {
                  files: diagnosticsByProperty[property.id],
                  notApplicable: property.not_applicable_diagnostics,
                  ancestors: toSources(ancestors),
                  requirement: requirements[tag],
                }),
              ]),
            ),
          });
        });
    };
    walk(tree.property_folders, tree.properties, 0, []);
    return result;
  }, [
    properties,
    propertyFolders,
    diagnosticsByProperty,
    diagnosticsByFolder,
    search,
    sort,
    toSources,
  ]);

  // Colonnes utiles : au moins un bien affiché est concerné par le diagnostic,
  // ou un immeuble porte un document de ce type. Sans aucun bien, tout est montré.
  const visibleTags = useMemo(() => {
    const kept = DIAGNOSTIC_TAG_LIST.filter((tag) =>
      allRows.some((row) =>
        row.kind === "property"
          ? row.statuses[tag].status !== "nonConcerne"
          : row.statuses[tag].status !== "manquant",
      ),
    );
    return allRows.some((row) => row.kind === "property")
      ? kept
      : DIAGNOSTIC_TAG_LIST;
  }, [allRows]);

  const counts = useMemo<DiagnosticsCounts>(() => {
    const result: DiagnosticsCounts = {
      required: 0,
      valide: 0,
      manquant: 0,
      expire: 0,
      bientotExpire: 0,
    };
    allRows.forEach((row) => {
      if (row.kind !== "property") return;
      DIAGNOSTIC_TAG_LIST.forEach((tag) => {
        const { status } = row.statuses[tag];
        if (status === "nonConcerne") return;
        result.required += 1;
        result[status] += 1;
      });
    });
    return result;
  }, [allRows]);

  // Lignes affichées : les biens dont un diagnostic quelconque a un statut du
  // filtre global, et dont chaque colonne filtrée a un statut choisi pour elle ;
  // les dossiers qui contiennent encore un bien.
  const rows = useMemo(() => {
    const kept = allRows.filter(
      (row) =>
        row.kind === "property" &&
        DIAGNOSTIC_TAG_LIST.some((tag) =>
          matchesStatusFilter(row.statuses[tag], statusFilter),
        ) &&
        DIAGNOSTIC_TAG_LIST.every((tag) =>
          matchesStatusFilter(row.statuses[tag], columnFilters[tag] ?? []),
        ),
    );
    return allRows.filter((row) =>
      row.kind === "property"
        ? kept.includes(row)
        : kept.some((property) => property.ancestors.includes(row.folder)),
    );
  }, [allRows, statusFilter, columnFilters]);

  const propertyCount = rows.filter((row) => row.kind === "property").length;

  const toggleFolder = (folderId: number) =>
    setCollapsedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });

  const isHidden = (row: Row) =>
    row.ancestors.some((folder) => collapsedFolders.has(folder.id));

  const exportCsv = () =>
    downloadCsv(`diagnostics-${moment().format("YYYY-MM-DD")}.csv`, [
      [
        "Immeuble",
        "Bien",
        ...visibleTags.map((tag) => DIAGNOSTIC_SHORT_LABELS[tag]),
      ],
      ...rows.map((row) =>
        row.kind === "folder"
          ? [
              row.folder.nom,
              "",
              ...visibleTags.map((tag) =>
                row.statuses[tag].status === "manquant"
                  ? ""
                  : getExportText(row.statuses[tag]),
              ),
            ]
          : [
              row.ancestors[0]?.nom ?? "",
              row.property.nom,
              ...visibleTags.map((tag) => getExportText(row.statuses[tag])),
            ],
      ),
    ]);

  const refresh = () => {
    mutateProperties();
    mutateFolders();
  };

  const renderCell = (
    diagnostic: PropertyDiagnostic,
    onClick: () => void,
    { isFolder }: { isFolder: boolean },
  ) => {
    if (diagnostic.status === "manquant") {
      return (
        <StatusCell
          variant="add"
          onClick={onClick}
          tooltip={
            isFolder
              ? "Déposer un diagnostic valable pour tout l'immeuble"
              : diagnostic.reason
          }
        />
      );
    }
    if (diagnostic.status === "nonConcerne") {
      return (
        <StatusCell
          variant="muted"
          text={getNonConcerneLabel(diagnostic)}
          onClick={onClick}
          tooltip={getTooltip(diagnostic)}
        />
      );
    }
    if (diagnostic.inheritedFrom) {
      return (
        <StatusCell
          variant="badge"
          color="info"
          icon={IconLink}
          text="Hérité"
          onClick={onClick}
          tooltip={getTooltip(diagnostic)}
        />
      );
    }
    return (
      <StatusCell
        variant="badge"
        color={STATUS_COLORS[diagnostic.status]}
        text={getStatusText(diagnostic)}
        onClick={onClick}
        tooltip={getTooltip(diagnostic)}
      />
    );
  };

  return (
    <>
      {selected && (
        <DiagnosticFilesDialog
          key={`${selected.property?.id ?? ""}-${
            selected.propertyFolder?.id ?? ""
          }-${selected.tag}`}
          open
          onOpenChange={(open) => {
            if (!open) setSelected(null);
          }}
          property={selected.property}
          propertyFolder={selected.propertyFolder}
          tag={selected.tag}
          label={getDocumentLabel(selected.tag)}
          files={
            (selected.property
              ? diagnosticsByProperty[selected.property.id]
              : diagnosticsByFolder[selected.propertyFolder?.id ?? -1]) ?? []
          }
          ancestors={toSources(selected.ancestors)}
          requirement={selected.requirement}
          notApplicable={selected.property?.not_applicable_diagnostics?.includes(
            selected.tag,
          )}
          onToggleNotApplicable={
            selected.property &&
            (async (notApplicable) => {
              const property = selected.property as Property;
              const current = property.not_applicable_diagnostics ?? [];
              const next = notApplicable
                ? [...current, selected.tag]
                : current.filter((tag) => tag !== selected.tag);
              await updateProperty(property.id, {
                not_applicable_diagnostics: next,
              });
              setSelected({
                ...selected,
                property: { ...property, not_applicable_diagnostics: next },
              });
            })
          }
          onChanged={refresh}
        />
      )}
      <PilotageHeader
        title="Diagnostics immobiliers"
        actions={
          <Button
            variant="secondary"
            icon={IconDownload}
            disabled={rows.length === 0}
            onClick={exportCsv}
          >
            Exporter
          </Button>
        }
      />
      <DiagnosticsKpis
        counts={counts}
        activeStatus={statusFilter}
        onToggleStatus={(status) =>
          setStatusFilter((prev) =>
            prev.length === 1 && prev[0] === status ? [] : [status],
          )
        }
      />
      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Rechercher un bien, un immeuble"
        activeFiltersCount={
          (statusFilter.length > 0 ? 1 : 0) +
          (hasColumnFilter ? 1 : 0) +
          bienFilterCount
        }
        onClearFilters={() => {
          setStatusFilter([]);
          setColumnFilters({});
          onClearBienFilter();
        }}
      >
        <FilterSection
          title="Statut"
          options={STATUS_FILTER_OPTIONS}
          values={statusFilter}
          onChange={setStatusFilter}
          defaultOpen
        />
        {bienFilter}
      </FilterBar>
      <Table variant="card" stickyFirstColumn>
        <Table.Header>
          <Table.Column>
            <div className={styles.firstHeader}>
              <Table.SortButton
                direction={directionFor("nom")}
                onSort={() => toggleSort("nom")}
              >
                Bien
              </Table.SortButton>
              <Text as="span" color="muted">
                {propertyCount} bien{propertyCount > 1 ? "s" : ""}
              </Text>
            </div>
          </Table.Column>
          {visibleTags.map((tag) => (
            <Table.Column key={tag}>
              <FilterableColumnHeader
                label={DIAGNOSTIC_SHORT_LABELS[tag]}
                options={STATUS_FILTER_OPTIONS}
                values={columnFilters[tag] ?? []}
                onChange={(values) =>
                  setColumnFilters((prev) => ({ ...prev, [tag]: values }))
                }
              />
            </Table.Column>
          ))}
        </Table.Header>
        <Table.Body>
          {rows.map((row) => {
            if (isHidden(row)) return null;
            const indent = { "--depth": row.depth } as CSSProperties;
            return row.kind === "folder" ? (
              <Table.Row key={`folder-${row.folder.id}`}>
                <Table.Cell>
                  <div
                    className={classnames(
                      styles.name,
                      row.depth > 0 && styles.nested,
                    )}
                    style={indent}
                  >
                    <button
                      type="button"
                      className={styles.chevron}
                      onClick={() => toggleFolder(row.folder.id)}
                      aria-expanded={!collapsedFolders.has(row.folder.id)}
                      aria-label={
                        collapsedFolders.has(row.folder.id)
                          ? `Déplier ${row.folder.nom}`
                          : `Replier ${row.folder.nom}`
                      }
                    >
                      {collapsedFolders.has(row.folder.id) ? (
                        <IconChevronRight size={18} stroke={1.5} />
                      ) : (
                        <IconChevronDown size={18} stroke={1.5} />
                      )}
                    </button>
                    <IconBuilding
                      size={18}
                      stroke={1.5}
                      className={styles.folderIcon}
                    />
                    <Text as="span" weight="bold">
                      {row.folder.nom}
                    </Text>
                  </div>
                </Table.Cell>
                {visibleTags.map((tag) => (
                  <Table.Cell key={tag}>
                    {renderCell(
                      row.statuses[tag],
                      () =>
                        setSelected({
                          tag,
                          propertyFolder: row.folder,
                          ancestors: [],
                        }),
                      { isFolder: true },
                    )}
                  </Table.Cell>
                ))}
              </Table.Row>
            ) : (
              <Table.Row key={`property-${row.property.id}`}>
                <Table.Cell>
                  <div
                    className={classnames(
                      styles.name,
                      row.depth > 0 && styles.nested,
                    )}
                    style={indent}
                  >
                    <Link
                      to={`/bailleur/properties/${row.property.id}?tab-prop-show=documents`}
                      className={styles.propertyLink}
                    >
                      <Text as="span" weight="medium">
                        {row.property.nom}
                      </Text>
                    </Link>
                  </div>
                </Table.Cell>
                {visibleTags.map((tag) => (
                  <Table.Cell key={tag}>
                    {renderCell(
                      row.statuses[tag],
                      () =>
                        setSelected({
                          tag,
                          property: row.property,
                          ancestors: row.ancestors,
                          requirement: row.requirements[tag],
                        }),
                      { isFolder: false },
                    )}
                  </Table.Cell>
                ))}
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </>
  );
}
